import { Box, Divider, Menu, MenuItem } from '@mui/material';
import { ArrowLeftRight, LogOut } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import type { PatientSummaryDto } from '~/backend/connector-next-api/connector-next-schemas';
import BreadcrumbButton from '~/components/Buttons/BreadcrumbButton';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import MenuFlyoutRow from '~/components/Menu/MenuFlyoutRow';
import PatientProperties from '~/components/Properties/PatientProperties';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { formatPatientName } from '~/containers/patient-list/model';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { Patient } from '~/domain/patient/patient';
import { useConnectorIdentity } from '~/hooks/auth/useConnectorIdentity';
import { useCloseAndOpenPatient } from '~/hooks/openInViewer/useCloseAndOpenPatient';
import useSensitiveInfoReveal from '~/hooks/patient/useSensitiveInfoReveal';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import {
    pendingEntryCount,
    usePendingChanges,
} from '~/hooks/save/usePendingChanges';
import { useSavePatientChanges } from '~/hooks/save/useSavePatientChanges';
import useBreadcrumbMenu from '~/hooks/useBreadcrumbMenu';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { calculateAge, formatDateCompact } from '~/utils/date';
import PatientInfoHiddenChip from '../PatientInfoHiddenChip';
import ReturnToListButton from '../ReturnToListButton';
import { useReturnToPatientList } from '../useReturnToPatientList';
import AssignedPatientsFlyout from './AssignedPatientsFlyout';
import PatientCourseFacts from './PatientCourseFacts';
import SensitiveInfoToggle from './SensitiveInfoToggle';

interface PatientMenuProps {
    patient: Patient;
}

/** What closing the loaded patient leads to. Each is confirmed on the same terms. */
type Closing =
    | { kind: 'returnToList' }
    | { kind: 'close' }
    | { kind: 'switch'; patientId: string; patient: PatientSummaryDto };

/** The wording of each closing's confirmation. */
const CLOSING_MESSAGE_IDS: Record<
    Closing['kind'],
    {
        title: string;
        message: string;
        messageUnsaved: string;
        confirm: string;
        confirmUnsaved: string;
        saveAndConfirm: string;
    }
> = {
    returnToList: {
        title: 'backToPatientList',
        message: 'backToPatientListConfirmation',
        messageUnsaved: 'backToPatientListConfirmationUnsaved',
        confirm: 'close',
        confirmUnsaved: 'closeWithoutSaving',
        saveAndConfirm: 'saveChanges.saveAndClose',
    },
    close: {
        title: 'closePatient',
        message: 'closePatientConfirmation',
        messageUnsaved: 'closePatientConfirmationUnsaved',
        confirm: 'close',
        confirmUnsaved: 'closeWithoutSaving',
        saveAndConfirm: 'saveChanges.saveAndClose',
    },
    switch: {
        title: 'switchPatient',
        message: 'switchPatient.confirmation',
        messageUnsaved: 'switchPatient.confirmationUnsaved',
        confirm: 'switchPatient.confirm',
        confirmUnsaved: 'switchWithoutSaving',
        saveAndConfirm: 'switchPatient.saveAndSwitch',
    },
};

/**
 * The patient card in the header and its menu. Every way of closing the
 * patient, the arrow back included, asks first on the same terms.
 */
export default function PatientMenu({ patient }: PatientMenuProps) {
    const { buttonProps, menuProps, close } = useBreadcrumbMenu();
    const intl = useIntl();
    const {
        isInfoShown: showSensitiveInfo,
        isConfirmOpen: revealConfirmOpen,
        requestReveal,
        confirmReveal,
        cancelReveal,
        hideInfo,
    } = useSensitiveInfoReveal();
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesState);
    const { unloadCurrentPatient } = useUnloadPatient();
    const { available: canReturnToList, returnToList } =
        useReturnToPatientList();
    const closeAndOpen = useCloseAndOpenPatient();
    const { signInEnabled } = useConnectorIdentity();
    const connectorPatientId =
        useSessionOrNull()?.persistenceRegistry.patientRef?.patientId;
    const { state: pendingChanges, canSave } = usePendingChanges();
    const save = useSavePatientChanges();
    // Kept after the confirmation closes, so its wording holds while it fades.
    const [closing, setClosing] = useState<Closing>({ kind: 'close' });
    const [confirmClosingOpen, setConfirmClosingOpen] = useState(false);
    const [switchRowEl, setSwitchRowEl] = useState<HTMLLIElement | null>(null);
    const [switchOpen, setSwitchOpen] = useState(false);

    // Switching runs the open from the patient list, so it is offered only
    // where the way back is; and it lists the user's own patients, which
    // exist only where the Connector identifies its users.
    const canSwitch = canReturnToList && signInEnabled;

    // "Save and close" is offered when there are pending dataset changes
    // this session can actually persist. Out-of-scope changes (dose display
    // settings, plans) cannot be saved and are lost either way — the base
    // warning covers them.
    const canSaveAndClose = canSave && pendingEntryCount(pendingChanges) > 0;

    const patientName = patient.dataSet.PatientName || 'Patient';
    const patientId = patient.dataSet.PatientID || 'N/A';
    const patientSexCode = patient.dataSet.PatientSex;
    const patientDob = patient.dataSet.PatientBirthDate;
    const formattedDob = patientDob ? formatDateCompact(patientDob) : undefined;
    const age = patientDob ? calculateAge(patientDob) : undefined;
    const dobWithAge =
        formattedDob && age !== undefined
            ? `${formattedDob} (${age}y)`
            : formattedDob;

    const sexMessageIds: Record<string, string> = {
        M: 'sex.male',
        F: 'sex.female',
        O: 'sex.other',
    };
    const patientSex = patientSexCode
        ? intl.formatMessage({
              id: sexMessageIds[patientSexCode.toUpperCase()] || 'sex.unknown',
          })
        : undefined;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key.toLowerCase() === 'h') {
                // preventDefault stops the input's Ctrl+H = backspace behavior
                // (and the browser's history-panel shortcut). stopPropagation
                // would be redundant because capture-phase listeners run
                // before the target's own handlers.
                event.preventDefault();
                if (showSensitiveInfo) {
                    hideInfo();
                    return;
                }
                if (revealConfirmOpen) {
                    return;
                }
                requestReveal();
            }
        };
        // Capture phase so the toggle fires even when focus is in a text
        // input that calls stopPropagation in its own onKeyDown.
        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => {
            document.removeEventListener('keydown', handleKeyDown, {
                capture: true,
            });
        };
    }, [showSensitiveInfo, revealConfirmOpen, hideInfo, requestReveal]);

    const closeMenu = useCallback(() => {
        setSwitchOpen(false);
        close();
    }, [close]);

    const requestClosing = useCallback(
        (requested: Closing) => {
            closeMenu();
            setClosing(requested);
            setConfirmClosingOpen(true);
        },
        [closeMenu]
    );

    const proceed = useCallback(
        (confirmed: Closing) => {
            switch (confirmed.kind) {
                case 'returnToList':
                    returnToList();
                    return;
                case 'close':
                    unloadCurrentPatient();
                    return;
                case 'switch':
                    // On the work case where the patient has one.
                    closeAndOpen(confirmed.patientId, { workCase: true });
                    return;
            }
        },
        [returnToList, unloadCurrentPatient, closeAndOpen]
    );

    const handleConfirmClosing = useCallback(() => {
        setConfirmClosingOpen(false);
        proceed(closing);
    }, [proceed, closing]);

    /**
     * Saves everything, then closes only on full success. On a partial
     * failure or conflict the patient stays open — the save state is
     * shared across hook instances (saveAtoms), so conflicts from THIS
     * save open the standard conflict dialog rendered by the header
     * indicator; closing would discard the very changes the user just
     * asked to keep.
     */
    const handleSaveAndClose = useCallback(
        async (confirmed: Closing) => {
            const result = await save.saveAll();
            if (result?.allSaved) {
                setConfirmClosingOpen(false);
                // flushSync so the unload and its navigation commit
                // together, exactly like the synchronous confirm path.
                //
                // This once guarded something sharper: useWorkflowSteps
                // redirected to /setup the moment a step's requirements
                // went away, and that redirect clobbered returnToList's
                // navigate to the list, stranding the user in an empty
                // viewer. Steps are search parameters now and nothing
                // redirects, so what is left is only keeping the unload
                // and the navigation in one commit — likely removable,
                // but not on the word of this comment alone.
                flushSync(() => {
                    proceed(confirmed);
                });
            }
        },
        [save, proceed]
    );

    const closingWording = CLOSING_MESSAGE_IDS[closing.kind];
    const saveAndCloseAction = canSaveAndClose
        ? {
              label: intl.formatMessage({ id: closingWording.saveAndConfirm }),
              testId: 'save-and-close-button',
          }
        : undefined;

    return (
        <>
            {/* Sits before the patient card, as it does in the concept
                prototype. It lives here rather than beside the card because
                returning closes the patient, and the confirmation for that —
                including its unsaved-changes wording and its Save and close —
                is already owned here. A second copy of that dialog is how the
                two would come to disagree about what closing costs. */}
            {canReturnToList && (
                <ReturnToListButton
                    onClick={() => requestClosing({ kind: 'returnToList' })}
                />
            )}
            <BreadcrumbButton
                {...buttonProps}
                data-testid="patient-breadcrumb"
                flipped
                topContent={
                    showSensitiveInfo ? (
                        <SensitiveInfoTypography>
                            {patientName}
                        </SensitiveInfoTypography>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <PatientInfoHiddenChip />
                        </Box>
                    )
                }
                label={
                    showSensitiveInfo ? (
                        <SensitiveInfoTypography
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            {[patientSex, patientId, dobWithAge]
                                .filter(Boolean)
                                .flatMap((item, index, arr) =>
                                    index < arr.length - 1
                                        ? [
                                              item,
                                              <Divider
                                                  key={`divider-${index}`}
                                                  orientation="vertical"
                                                  flexItem
                                                  sx={{
                                                      mx: 0.5,
                                                      height: 12,
                                                  }}
                                              />,
                                          ]
                                        : [item]
                                )}
                        </SensitiveInfoTypography>
                    ) : null
                }
            />

            <Menu {...menuProps} onClose={closeMenu}>
                <Box position="relative" px={1} pt={1} pr={5}>
                    <Box
                        sx={
                            !showSensitiveInfo
                                ? {
                                      filter: 'blur(0.5rem)',
                                      userSelect: 'none',
                                  }
                                : undefined
                        }
                    >
                        <PatientProperties
                            dataSet={patient.dataSet}
                            showTitle={false}
                        />
                    </Box>
                    <Box position="absolute" top={0} right={0}>
                        <SensitiveInfoToggle />
                    </Box>
                </Box>

                {connectorPatientId && <Divider flexItem sx={{ my: 0.5 }} />}
                {connectorPatientId && (
                    <Box px={1} py={0.5}>
                        <PatientCourseFacts patientId={connectorPatientId} />
                    </Box>
                )}

                <Divider flexItem sx={{ my: 0.5 }} />

                {canSwitch && (
                    <MenuFlyoutRow
                        ref={setSwitchRowEl}
                        open={switchOpen}
                        onToggle={() => setSwitchOpen((open) => !open)}
                        onOpen={() => setSwitchOpen(true)}
                        Icon={ArrowLeftRight}
                        name={<FormattedMessage id="switchPatient" />}
                        data-testid="switch-patient-row"
                    />
                )}

                <MenuItem
                    onClick={() => requestClosing({ kind: 'close' })}
                    sx={{ fontWeight: 500 }}
                    data-testid="close-patient-menu-item"
                >
                    <FormattedMessage id="closePatient" />
                    <LogOut
                        style={{ marginLeft: 'auto', fontSize: 12 }}
                        size={12}
                    />
                </MenuItem>
            </Menu>
            {/* A sibling of the menu, not a child: a child would hand its
                arrow keys to the menu too, which then moves focus twice. */}
            {canSwitch && (
                <AssignedPatientsFlyout
                    anchorEl={switchRowEl}
                    open={menuProps.open && switchOpen}
                    onClose={() => setSwitchOpen(false)}
                    excludedPatientId={connectorPatientId}
                    onChoose={(chosen, patientId) =>
                        requestClosing({
                            kind: 'switch',
                            patientId,
                            patient: chosen,
                        })
                    }
                />
            )}

            <ConfirmationDialog
                open={confirmClosingOpen}
                onCancel={() => setConfirmClosingOpen(false)}
                onConfirm={handleConfirmClosing}
                busy={save.saving}
                extraAction={
                    saveAndCloseAction && {
                        ...saveAndCloseAction,
                        onClick: () => void handleSaveAndClose(closing),
                    }
                }
                title={intl.formatMessage({ id: closingWording.title })}
                message={
                    <FormattedMessage
                        id={
                            hasUnsavedChanges
                                ? closingWording.messageUnsaved
                                : closingWording.message
                        }
                        values={{
                            patient: closing.kind === 'switch' && (
                                <SwitchTarget patient={closing.patient} />
                            ),
                        }}
                    />
                }
                confirmText={intl.formatMessage({
                    id: hasUnsavedChanges
                        ? closingWording.confirmUnsaved
                        : closingWording.confirm,
                })}
            />

            <ConfirmationDialog
                open={revealConfirmOpen}
                onCancel={cancelReveal}
                onConfirm={confirmReveal}
                mode="warning"
                title={intl.formatMessage({
                    id: 'revealPatientInfoConfirmationTitle',
                })}
                message={intl.formatMessage({
                    id: 'revealPatientInfoConfirmation',
                })}
            />
        </>
    );
}

/**
 * The patient a switch opens, by name and full patient ID, the two the user
 * checks it against; hidden with the rest of the patient information. A
 * patient with neither is said to have neither, never left unnamed.
 */
function SwitchTarget({ patient }: { patient: PatientSummaryDto }) {
    const intl = useIntl();
    const name = formatPatientName(patient.dicomPatientName);
    const id = patient.dicomPatientId;
    return (
        <SensitiveInfoTypography>
            {name && id
                ? `${name} (${id})`
                : name ||
                  id ||
                  intl.formatMessage({ id: 'switchPatient.unidentified' })}
        </SensitiveInfoTypography>
    );
}
